import { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { showError, showSuccess } from '../utils/alerts';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaCheck, FaClock } from 'react-icons/fa';
import '../styles/pages/ManagePaymentsPage.css';

/** Monto en pesos chilenos: $45.000 (con decimales solo si los tiene). */
const formatoPesos = (valor) => `$${Number(valor || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 })}`;

const ManagePaymentsPage = () => {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const [cliente, setCliente] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMonth, setEditingMonth] = useState(null);
  const [editForm, setEditForm] = useState({
    pagado: false,
    medio_de_pago: '',
    monto: '',
    id_pago: '',
    fecha_pago: '',
    notas: ''
  });

  const months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Generar años disponibles (desde 2020 hasta año actual + 1)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2019 + 2 }, (_, i) => 2020 + i);

  useEffect(() => {
    fetchClienteData();
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, selectedYear]);

  const fetchClienteData = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/clientes/${clienteId}`,
        { withCredentials: true }
      );
      setCliente(response.data);
    } catch (error) {
      console.error('Error al obtener datos del cliente:', error);
      showError('Error al cargar información del cliente');
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/clientes/${clienteId}/pagos/${selectedYear}`,
        { withCredentials: true }
      );
      setPayments(response.data);
    } catch (error) {
      console.error('Error al obtener pagos:', error);
      showError('Error al cargar pagos');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentForMonth = (monthNumber) => {
    return payments.find(p => p.mes === monthNumber) || null;
  };

  const handleEditClick = (monthNumber) => {
    const payment = getPaymentForMonth(monthNumber);
    if (payment) {
      setEditForm({
        pagado: payment.pagado,
        medio_de_pago: payment.medio_de_pago || '',
        monto: payment.monto || '',
        id_pago: payment.id_pago || '',
        fecha_pago: payment.fecha_pago ? payment.fecha_pago.split('T')[0] : '',
        notas: payment.notas || ''
      });
    } else {
      setEditForm({
        pagado: false,
        medio_de_pago: '',
        monto: '',
        id_pago: '',
        fecha_pago: '',
        notas: ''
      });
    }
    setEditingMonth(monthNumber);
  };

  const handleSavePayment = async () => {
    try {
      // Validar que el monto sea mayor o igual a cero si se proporciona
      if (editForm.monto !== '' && editForm.monto !== null && parseFloat(editForm.monto) < 0) {
        showError('El monto debe ser mayor o igual a cero');
        return;
      }

      await axios.put(
        `${API_BASE_URL}/api/admin/clientes/${clienteId}/pagos`,
        {
          mes: editingMonth,
          año: selectedYear,
          ...editForm
        },
        { withCredentials: true }
      );

      showSuccess('Pago actualizado exitosamente');
      setEditingMonth(null);
      fetchPayments();
    } catch (error) {
      console.error('Error al guardar pago:', error);
      showError(error.response?.data?.message || 'Error al guardar pago');
    }
  };

  const handleCancelEdit = () => {
    setEditingMonth(null);
    setEditForm({
      pagado: false,
      medio_de_pago: '',
      monto: '',
      id_pago: '',
      fecha_pago: '',
      notas: ''
    });
  };

  // Meses ya vencidos del año elegido: todos si el año pasó, hasta el actual si es este año, ninguno si es futuro
  const hoy = new Date();
  const mesesVencidos = selectedYear < hoy.getFullYear() ? 12 : selectedYear === hoy.getFullYear() ? hoy.getMonth() + 1 : 0;
  const esFuturo = (monthNumber) => monthNumber > mesesVencidos;

  // Resumen del año
  const pagados = payments.filter(p => p.pagado);
  const pendientes = months.filter((_, index) => index + 1 <= mesesVencidos && !getPaymentForMonth(index + 1)?.pagado).length;
  const totalPagado = pagados.reduce((suma, p) => suma + (Number(p.monto) || 0), 0);

  return (
    <div className="manage-payments-page">
      <div className="page-header">
        <button onClick={() => navigate('/admin/clientes')} className="back-button">
          <FaArrowLeft /> Volver a Clientes
        </button>
        <h1>Gestión de Pagos</h1>
      </div>

      {cliente && (
        <div className="client-info">
          <h2>{cliente.nombre}</h2>
          <p>Email: {cliente.correo}</p>
          <p>Plan: {cliente.maximo_empresas} empresa(s)</p>
        </div>
      )}

      <div className="year-selector">
        <label htmlFor="year">Año:</label>
        <select
          id="year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        {!loading && (
          <div className="payments-summary">
            <span className="summary-item summary-item--ok">
              <FaCheck /> <strong>{pagados.length}</strong> {pagados.length === 1 ? 'pagado' : 'pagados'}
            </span>
            <span className="summary-item summary-item--pend">
              <FaClock /> <strong>{pendientes}</strong> {pendientes === 1 ? 'pendiente' : 'pendientes'}
            </span>
            <span className="summary-item">
              Total pagado <strong>{formatoPesos(totalPagado)}</strong>
            </span>
          </div>
        )}
      </div>

      {loading ? (
        <p>Cargando pagos...</p>
      ) : (
        <div className="payments-grid">
          {months.map((monthName, index) => {
            const monthNumber = index + 1;
            const payment = getPaymentForMonth(monthNumber);
            const isEditing = editingMonth === monthNumber;

            const futuro = !payment?.pagado && esFuturo(monthNumber);

            return (
              <div key={monthNumber} className={`payment-card ${payment?.pagado ? 'paid' : 'unpaid'}${futuro ? ' future' : ''}`}>
                <div className="month-header">
                  <h3>{monthName}</h3>
                  {!isEditing && (
                    <button
                      onClick={() => handleEditClick(monthNumber)}
                      className="edit-button"
                      title="Editar"
                    >
                      <FaEdit />
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="edit-form">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={editForm.pagado}
                          onChange={(e) => setEditForm({ ...editForm, pagado: e.target.checked })}
                        />
                        {' '}Pagado
                      </label>
                    </div>

                    <div className="form-group">
                      <label>Monto:</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editForm.monto}
                        onChange={(e) => setEditForm({ ...editForm, monto: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="form-group">
                      <label>Medio de Pago:</label>
                      <select
                        value={editForm.medio_de_pago}
                        onChange={(e) => setEditForm({ ...editForm, medio_de_pago: e.target.value })}
                      >
                        <option value="">Seleccionar...</option>
                        <option value="Transferencia">Transferencia</option>
                        <option value="Tarjeta">Tarjeta</option>
                        <option value="Efectivo">Efectivo</option>
                        <option value="PayPal">PayPal</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>ID Comprobante:</label>
                      <input
                        type="text"
                        value={editForm.id_pago}
                        onChange={(e) => setEditForm({ ...editForm, id_pago: e.target.value })}
                        placeholder="ID del comprobante"
                      />
                    </div>

                    <div className="form-group">
                      <label>Fecha de Pago:</label>
                      <input
                        type="date"
                        value={editForm.fecha_pago}
                        onChange={(e) => setEditForm({ ...editForm, fecha_pago: e.target.value })}
                      />
                    </div>

                    <div className="form-group">
                      <label>Notas:</label>
                      <textarea
                        value={editForm.notas}
                        onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })}
                        placeholder="Notas adicionales..."
                        rows="2"
                      />
                    </div>

                    <div className="form-actions">
                      <button onClick={handleSavePayment} className="save-button">
                        Guardar
                      </button>
                      <button onClick={handleCancelEdit} className="cancel-button">
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="payment-info">
                    <div className="status-badge">
                      {payment?.pagado ? (
                        <span className="badge badge-success"><FaCheck /> Pagado</span>
                      ) : futuro ? (
                        <span className="badge badge-muted">Por vencer</span>
                      ) : (
                        <span className="badge badge-danger"><FaClock /> Pendiente</span>
                      )}
                    </div>

                    {/* El medio de pago y el comprobante se registran al editar, pero no se muestran en la grilla */}
                    {payment && (
                      <>
                        {payment.monto && (
                          <p><strong>Monto</strong> {formatoPesos(payment.monto)}</p>
                        )}
                        {payment.fecha_pago && (
                          <p><strong>Fecha</strong> {new Date(payment.fecha_pago).toLocaleDateString('es-CL')}</p>
                        )}
                        {payment.notas && (
                          <p className="notes"><strong>Notas:</strong> {payment.notas}</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManagePaymentsPage;
